import { Check, Calendar } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

interface ServicesProps {
  cityName?: string;
}

export const Services = ({ cityName = "Long Island" }: ServicesProps) => {
  const citySlug = cityName.toLowerCase().replace(/\s+/g, '-');
  
  const serviceCategories = [
    { name: "Roofing Services", slug: "roofing", featured: true },
    { name: "Emergency Roof Repair", slug: "emergency-roof-repair", featured: true },
    { name: "Gutter Services", slug: "gutter-repair", featured: true },
    { name: "Chimney Services", slug: "chimney-repair", featured: true },
  ];

  const services = [
    "Roof Repair",
    "Roof Installation", 
    "Roof Replacement",
    "Roof Leak Repair",
    "Roof Tarp Installation",
    "Roof Flashing Repair",
    "Roof Vent Pipe Repair",
    "Roof Plywood Repair",
    "Emergency Roof Repair",
    "Skylight Repair",
    "Skylight Replacement",
    "Skylight Leak Repair"
  ];

  const features = [
    "Industry-leading warranties",
    "GAF Certified Roofing Contractor",
    "Professional and responsive"
  ];

  return (
    <section id="services" className="py-12 md:py-20 bg-background">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center max-w-4xl mx-auto mb-12 md:mb-16">
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4 md:mb-6">
            Expert Roofing Solutions in {cityName}
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground leading-relaxed">
            Our Roofing Services in {cityName} provide comprehensive solutions to protect your property with quality craftsmanship and durable materials. Whether you're looking to install a new roof or repair an existing one, our expert team is here to help with a wide range of services:
          </p>
        </div>

        {/* Featured Service Categories */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-12 md:mb-16">
          {serviceCategories.map((category) => (
            <Link 
              key={category.slug}
              to={`/${category.slug}/${citySlug}`}
              className="group"
            >
              <Card className="border-2 border-primary/20 hover:border-primary transition-all duration-200 hover:shadow-lg bg-card h-full">
                <CardContent className="p-6 text-center space-y-3">
                  <h3 className="font-bold text-lg text-card-foreground group-hover:text-primary transition-colors">
                    {category.name}
                  </h3>
                  <p className="text-sm text-muted-foreground">Learn More →</p>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 mb-12 md:mb-16">
          {services.map((service, index) => (
            <Card key={index} className="border border-border/50 hover:border-primary/50 transition-all duration-200 hover:shadow-md bg-card/50 backdrop-blur-sm">
              <CardContent className="p-4 sm:p-6 flex items-center space-x-3 sm:space-x-4">
                <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <Check className="w-4 h-4 text-primary" />
                </div>
                <h3 className="font-semibold text-card-foreground text-sm sm:text-base">{service}</h3>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Company Description */}
        <div className="text-center max-w-4xl mx-auto mb-8 md:mb-12">
          <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
            We specialize in comprehensive home improvement services, including roofing, siding, chimneys, gutters, skylights, and more. Committed to quality over quantity, we ensure top-notch craftsmanship and exceptional service to keep your home protected and looking its best.
          </p>
        </div>

        {/* Features */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-8 mb-8">
          {features.map((feature, index) => (
            <div key={index} className="flex items-center justify-center space-x-3 p-4 sm:p-6 rounded-lg bg-primary/5 border border-primary/10">
              <Check className="w-5 h-5 text-primary flex-shrink-0" />
              <span className="font-semibold text-foreground text-center text-sm sm:text-base">{feature}</span>
            </div>
          ))}
        </div>

        {/* Book Appointment CTA */}
        <div className="text-center">
          <Button
            size="lg"
            className="font-semibold px-8 py-6 text-lg shadow-lg hover:shadow-xl transition-all duration-200"
            asChild
          >
            <a href="/book-appointment">
              <Calendar className="w-5 h-5 mr-2" />
              Schedule Your Free Estimate
            </a>
          </Button>
        </div>
      </div>
    </section>
  );
};